import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import {
  ArrowLeft, Loader2, RefreshCw, Building2, X, Check, Sparkles,
  ChevronDown, ChevronUp, Mail, Users, CheckSquare, Square,
} from 'lucide-react';
import { req } from '../services/api';

// ─── Componente inline de acción por dominio ───────────────────────────────
function DominioAccionPanel({ dominio, onDecision, onCancelar, modoInicial = 'buscar' }) {
  const [modo, setModo] = useState(modoInicial); // 'buscar' | 'crear'

  // Buscar empresa
  const [query, setQuery] = useState('');
  const [resultados, setResultados] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [seleccionada, setSeleccionada] = useState(null);
  const [aplicando, setAplicando] = useState(false);
  const timerRef = useRef(null);

  // Crear empresa
  const [formCrear, setFormCrear] = useState({
    nombre: '',
    cif: '',
    dominio_email: dominio,
  });
  const [cifExistente, setCifExistente] = useState(null);
  const [buscandoCif, setBuscandoCif] = useState(false);
  const [creando, setCreando] = useState(false);
  const cifTimerRef = useRef(null);
  const [nombreSimilares, setNombreSimilares] = useState([]);
  const [buscandoNombre, setBuscandoNombre] = useState(false);
  const nombreTimerRef = useRef(null);
  const [analizandoIA, setAnalizandoIA] = useState(false);
  const [iaMsg, setIaMsg] = useState('');

  // Logo Clearbit
  const clearbitUrl = `https://logo.clearbit.com/${dominio}`;
  const [logoOk, setLogoOk] = useState(null); // null=cargando | true=ok | false=error

  const buscar = (q) => {
    setQuery(q);
    setSeleccionada(null);
    clearTimeout(timerRef.current);
    if (!q.trim()) { setResultados([]); return; }
    timerRef.current = setTimeout(async () => {
      setBuscando(true);
      try {
        const d = await req(`/api/entidades/autocomplete?q=${encodeURIComponent(q.trim())}&limit=6`);
        setResultados(Array.isArray(d) ? d : (d.items || []));
      } catch (_) { setResultados([]); }
      finally { setBuscando(false); }
    }, 300);
  };

  const confirmarVincular = async () => {
    if (!seleccionada) return;
    setAplicando(true);
    try {
      await req('/api/correo/ingesta/aplicar-dominio', {
        method: 'POST',
        body: JSON.stringify({ domain: dominio, decision: 'vincular', entidad_id: seleccionada.id }),
      });
      onDecision(dominio, 'vincular', seleccionada.nombre);
    } catch (e) {
      toast.error(`Vincular: ${e.message}`);
    } finally { setAplicando(false); }
  };

  const verificarCif = (cif) => {
    clearTimeout(cifTimerRef.current);
    setCifExistente(null);
    if (cif.trim().length < 5) return;
    cifTimerRef.current = setTimeout(async () => {
      setBuscandoCif(true);
      try {
        const d = await req(`/api/entidades/autocomplete?q=${encodeURIComponent(cif.trim())}&limit=3`);
        const its = Array.isArray(d) ? d : (d.items || []);
        const match = its.find((r) => (r.cif || '').toLowerCase() === cif.trim().toLowerCase());
        setCifExistente(match ? { id: match.id, nombre: match.nombre_comercial || match.razon_social } : null);
      } catch (_) { setCifExistente(null); }
      finally { setBuscandoCif(false); }
    }, 400);
  };

  const verificarNombre = (nombre) => {
    clearTimeout(nombreTimerRef.current);
    setNombreSimilares([]);
    if (nombre.trim().length < 3) return;
    nombreTimerRef.current = setTimeout(async () => {
      setBuscandoNombre(true);
      try {
        const d = await req(`/api/entidades/autocomplete?q=${encodeURIComponent(nombre.trim())}&limit=3`);
        setNombreSimilares(Array.isArray(d) ? d : (d.items || []));
      } catch (_) { setNombreSimilares([]); }
      finally { setBuscandoNombre(false); }
    }, 400);
  };

  const analizarConIA = async () => {
    if (!dominio) { toast.error('No hay dominio disponible'); return; }
    setAnalizandoIA(true); setIaMsg('');
    try {
      const r = await req('/api/perplexity/buscar-empresa', {
        method: 'POST',
        body: JSON.stringify({ razon_social: dominio }),
      });
      if (!r.razon_social && !r.cif && !r.web) {
        setIaMsg('Sin datos encontrados para este dominio.');
        return;
      }
      const qTermino = r.cif || r.razon_social || dominio;
      const d = await req(`/api/entidades/autocomplete?q=${encodeURIComponent(qTermino)}&limit=5`);
      const its = Array.isArray(d) ? d : (d.items || []);
      let existenteId = null; let existenteNombre = null;
      if (r.cif) {
        const match = its.find((x) => (x.cif || '').toUpperCase() === r.cif.toUpperCase());
        if (match) { existenteId = match.id; existenteNombre = match.nombre_comercial || match.razon_social; }
      }
      if (!existenteId && r.razon_social && its.length > 0) {
        existenteId = its[0].id; existenteNombre = its[0].nombre_comercial || its[0].razon_social;
      }
      if (existenteId) {
        setSeleccionada({ id: existenteId, nombre: existenteNombre });
        setIaMsg(`✓ Ya registrada en el CRM: ${existenteNombre}${r.cif ? ' · ' + r.cif : ''}`);
      } else {
        let dominioWeb = dominio;
        if (r.web) {
          try { dominioWeb = new URL(r.web.startsWith('http') ? r.web : `https://${r.web}`).hostname.replace(/^www\./, ''); } catch (_) {}
        }
        const nombreFinal = r.razon_social || dominio;
        setFormCrear({ nombre: nombreFinal, cif: r.cif || '', dominio_email: dominioWeb });
        if (r.cif) verificarCif(r.cif);
        verificarNombre(nombreFinal);
        setModo('crear');
      }
    } catch (e) {
      toast.error(`IA: ${e.message}`);
    } finally { setAnalizandoIA(false); }
  };

  const confirmarCrear = async () => {
    if (!formCrear.nombre.trim()) { toast.error('El nombre es obligatorio'); return; }
    if (cifExistente) { toast.error('Ese CIF ya existe — usa "Usar esta empresa".'); return; }
    setCreando(true);
    try {
      await req('/api/correo/ingesta/aplicar-dominio', {
        method: 'POST',
        body: JSON.stringify({
          domain: dominio,
          decision: 'crear',
          crear_empresa: {
            razon_social: formCrear.nombre.trim(),
            cif: formCrear.cif.trim() || undefined,
            dominio_email: formCrear.dominio_email.trim() || undefined,
            logo_url: logoOk === true ? clearbitUrl : undefined,
          },
        }),
      });
      onDecision(dominio, 'crear', formCrear.nombre.trim());
    } catch (e) {
      // 409 = CIF duplicado detectado en backend
      if (e.message?.includes('409') || e.message?.includes('Ya existe')) {
        toast.error('CIF duplicado — ya existe esa empresa en el sistema');
      } else {
        toast.error(`Crear empresa: ${e.message}`);
      }
    } finally { setCreando(false); }
  };

  if (modo === 'crear') {
    return (
      <div className="mt-2 p-3 bg-white border border-orange-200 rounded-lg space-y-2">
        {/* img oculta para detectar si Clearbit tiene logo del dominio */}
        <img src={clearbitUrl} alt="" className="hidden"
          onLoad={() => setLogoOk(true)} onError={() => setLogoOk(false)} />
        <div className="flex items-center gap-2">
          <button type="button" className="text-[11px] text-ink-500 hover:text-ink-700"
            onClick={() => setModo('buscar')}>← Volver</button>
          <span className="text-xs font-semibold text-orange-800">Nueva empresa para @{dominio}</span>
          {/* Preview logo Clearbit */}
          <div className="ml-auto flex items-center gap-1.5">
            {logoOk === true && (
              <img src={clearbitUrl} alt="logo" className="w-7 h-7 rounded object-contain border border-ink-100"
                title="Logo detectado automáticamente" />
            )}
            {logoOk === false && (
              <div className="w-7 h-7 rounded bg-ink-100 flex items-center justify-center" title="Sin logo detectado">
                <Building2 className="w-4 h-4 text-ink-400" />
              </div>
            )}
            {logoOk === null && <Loader2 className="w-3.5 h-3.5 animate-spin text-ink-300" />}
            {logoOk === true && <span className="text-[10px] text-emerald-600">Logo detectado</span>}
          </div>
        </div>
        <div>
          <label className="text-[10px] text-ink-500 uppercase font-semibold block mb-0.5">Nombre *</label>
          <Input className="h-8 text-sm" placeholder="Razón social"
            value={formCrear.nombre}
            onChange={(e) => { setFormCrear((f) => ({ ...f, nombre: e.target.value })); verificarNombre(e.target.value); }}
            data-testid="ingesta-crear-nombre" />
          {(buscandoNombre || nombreSimilares.length > 0) && (
            <div className="mt-1 p-2 bg-amber-50 border border-amber-200 rounded text-[11px]">
              {buscandoNombre && <p className="text-ink-400">Verificando nombre…</p>}
              {!buscandoNombre && nombreSimilares.length > 0 && (
                <>
                  <p className="text-amber-800 font-semibold mb-1">⚠ Ya existen empresas similares:</p>
                  <ul className="space-y-1">
                    {nombreSimilares.map((s) => (
                      <li key={s.id} className="flex items-center justify-between gap-2">
                        <span className="text-ink-700">
                          {s.nombre_comercial || s.razon_social}
                          {s.cif && <span className="ml-1.5 text-ink-400">{s.cif}</span>}
                        </span>
                        <button type="button" className="text-brand-600 underline text-[10px] shrink-0"
                          onClick={() => { setSeleccionada({ id: s.id, nombre: s.nombre_comercial || s.razon_social }); setNombreSimilares([]); setModo('buscar'); }}>
                          Usar esta
                        </button>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          )}
        </div>
        <div>
          <label className="text-[10px] text-ink-500 uppercase font-semibold block mb-0.5">CIF / NIF <span className="text-amber-600">(recomendado)</span></label>
          <Input className={`h-8 text-sm ${cifExistente ? 'border-red-400' : ''}`}
            placeholder="B12345678" value={formCrear.cif}
            onChange={(e) => { setFormCrear((f) => ({ ...f, cif: e.target.value })); verificarCif(e.target.value); }}
            data-testid="ingesta-crear-cif" />
          {buscandoCif && <p className="text-[10px] text-ink-400 mt-0.5">Verificando CIF…</p>}
          {cifExistente && (
            <div className="mt-1 p-1.5 bg-red-50 border border-red-200 rounded text-[11px] text-red-800">
              Ya existe: <strong>{cifExistente.nombre}</strong>
              <button type="button" className="ml-2 underline text-red-700"
                onClick={() => { setSeleccionada({ id: cifExistente.id, nombre: cifExistente.nombre }); setModo('buscar'); }}>
                Usar esta empresa
              </button>
            </div>
          )}
          {!formCrear.cif.trim() && !cifExistente && (
            <p className="text-[10px] text-amber-600 mt-0.5">
              Sin CIF/NIF la empresa quedará pendiente de enriquecimiento y no aparecerá en la lista principal.
            </p>
          )}
        </div>
        <div>
          <label className="text-[10px] text-ink-500 uppercase font-semibold block mb-0.5">Dominio email</label>
          <Input className="h-8 text-sm" value={formCrear.dominio_email}
            onChange={(e) => setFormCrear((f) => ({ ...f, dominio_email: e.target.value }))}
            data-testid="ingesta-crear-dominio" />
        </div>
        <div className="flex justify-between items-center pt-1">
          <button type="button" className="text-[11px] text-ink-400 hover:text-ink-600"
            onClick={onCancelar}>Cancelar</button>
          <Button size="sm" className="h-7 text-xs bg-orange-600 hover:bg-orange-700"
            onClick={confirmarCrear} disabled={!formCrear.nombre.trim() || !!cifExistente || creando}
            data-testid="ingesta-crear-confirmar">
            {creando ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Crear y vincular'}
          </Button>
        </div>
      </div>
    );
  }

  // modo 'buscar'
  return (
    <div className="mt-2 p-3 bg-white border border-orange-200 rounded-lg">
      {/* Identificar empresa automáticamente con IA */}
      <div className="mb-3">
        <button type="button"
          className="w-full flex items-center justify-center gap-1.5 py-2 rounded border border-violet-200 bg-violet-50 text-xs font-medium text-violet-700 hover:bg-violet-100 transition disabled:opacity-40"
          onClick={analizarConIA} disabled={analizandoIA}
          data-testid="btn-analizar-ia-ingesta">
          {analizandoIA
            ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Buscando empresa automáticamente…</>
            : <><Sparkles className="w-3.5 h-3.5" /> Identificar empresa con IA</>}
        </button>
        {iaMsg ? (
          <p className={`text-[10px] mt-1 text-center ${iaMsg.startsWith('✓') ? 'text-emerald-600 font-medium' : 'text-amber-600'}`}>{iaMsg}</p>
        ) : (
          <p className="text-[10px] text-ink-400 text-center mt-0.5">Busca en bases de datos públicas usando @{dominio}</p>
        )}
      </div>
      <div className="relative">
        <Input className="h-8 text-sm" placeholder="O buscar empresa existente…"
          value={seleccionada ? seleccionada.nombre : query}
          onChange={(e) => buscar(e.target.value)}
          onFocus={() => { if (seleccionada) { setSeleccionada(null); setResultados([]); } }}
          data-testid="ingesta-buscar-empresa" />
        {(resultados.length > 0 || buscando) && !seleccionada && (
          <div className="absolute top-full left-0 right-0 z-[9999] bg-white border border-ink-200 rounded shadow-md mt-0.5 max-h-40 overflow-y-auto">
            {buscando && <div className="px-3 py-2 text-xs text-ink-500">Buscando…</div>}
            {resultados.map((r) => (
              <button key={r.id} type="button"
                className="w-full text-left px-3 py-2 text-xs hover:bg-brand-50 border-b border-ink-50 last:border-0"
                onClick={() => { setSeleccionada({ id: r.id, nombre: r.nombre_comercial || r.razon_social || r.id }); setResultados([]); }}
                data-testid={`ingesta-resultado-${r.id}`}>
                <span className="font-medium text-ink-900">{r.nombre_comercial || r.razon_social}</span>
                {r.cif && <span className="ml-1 text-ink-400">{r.cif}</span>}
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="flex items-center justify-between mt-2">
        <div className="flex items-center gap-3">
          <button type="button" className="text-[11px] text-brand-600 hover:underline"
            onClick={() => setModo('crear')} data-testid="ingesta-crear-nueva">
            ¿No encuentras la empresa? Crear nueva →
          </button>
          <button type="button" className="text-[11px] text-ink-400 hover:text-ink-600"
            onClick={onCancelar}>Cancelar</button>
        </div>
        <Button size="sm" className="h-7 text-xs bg-orange-600 hover:bg-orange-700"
          onClick={confirmarVincular} disabled={!seleccionada || aplicando}
          data-testid="ingesta-vincular-confirmar">
          {aplicando ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Vincular empresa'}
        </Button>
      </div>
    </div>
  );
}


// ─── Fila de dominio ───────────────────────────────────────────────────────
function DominioRow({ item, isSelected, onToggle, onDecision }) {
  const [expandido, setExpandido] = useState(false);
  const [modoPanel, setModoPanel] = useState('buscar'); // modo que verá DominioAccionPanel
  const [ignorando, setIgnorando] = useState(false);
  const [ignorarOpts, setIgnorarOpts] = useState(false); // dropdown abierto

  // Cierra el dropdown al hacer clic fuera
  useEffect(() => {
    if (!ignorarOpts) return;
    const close = () => setIgnorarOpts(false);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [ignorarOpts]);

  // Abre el panel en el modo indicado; si ya estaba abierto fuerza re-mount via key
  const abrirPanel = (modo) => {
    setModoPanel(modo);
    setExpandido(true);
  };

  // "Ignorar ahora" — solo frontend, el dominio reaparece en la próxima carga
  const ignorarAhora = () => {
    onDecision(item.dominio, 'ignorar_ahora');
  };

  // "No importar nunca" — persiste en backend via ingesta_domain_decisions
  const ignorarPermanente = async () => {
    setIgnorando(true);
    try {
      await req('/api/correo/ingesta/aplicar-dominio', {
        method: 'POST',
        body: JSON.stringify({ domain: item.dominio, decision: 'ignorar' }),
      });
      onDecision(item.dominio, 'ignorar_permanente');
    } catch (e) {
      toast.error(`Ignorar: ${e.message}`);
      setIgnorando(false);
    }
  };

  return (
    <div className="border border-ink-100 rounded-lg bg-white" data-testid={`dominio-row-${item.dominio}`}>
      <div className="flex items-center gap-3 px-4 py-3">
        {/* Checkbox */}
        <button type="button" onClick={() => onToggle(item.dominio)} className="shrink-0 text-ink-400 hover:text-brand-600">
          {isSelected ? <CheckSquare className="w-4 h-4 text-brand-600" /> : <Square className="w-4 h-4" />}
        </button>

        {/* Dominio */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-sm font-semibold text-ink-900">@{item.dominio}</span>
            <Badge className="bg-orange-100 text-orange-800 border-orange-200 border text-[10px]">
              {item.count} {item.count === 1 ? 'correo' : 'correos'}
            </Badge>
          </div>
          {(item.muestra_con_nombre?.length > 0 || item.remitentes?.length > 0) && (
            <p className="text-[11px] text-ink-500 mt-0.5 truncate">
              {(item.muestra_con_nombre?.length > 0
                ? item.muestra_con_nombre.map((r) =>
                    r.nombre ? `${r.nombre} <${r.email}>` : r.email
                  )
                : item.remitentes
              ).slice(0, 3).join(' · ')}
              {(item.muestra_con_nombre?.length || item.remitentes?.length || 0) > 3 && ` +${(item.muestra_con_nombre?.length || item.remitentes?.length) - 3}`}
            </p>
          )}
          {item.muestra_asuntos?.length > 0 && (
            <p className="text-[11px] text-ink-400 mt-0.5 truncate italic">
              "{item.muestra_asuntos[0]}"
              {item.muestra_asuntos.length > 1 && ` +${item.muestra_asuntos.length - 1}`}
            </p>
          )}
        </div>

        {/* Acciones */}
        <div className="flex items-center gap-2 shrink-0">
          {/* Vincular a empresa existente */}
          <Button size="sm" variant="outline"
            className="h-7 text-xs text-orange-700 border-orange-300 hover:bg-orange-50"
            onClick={() => abrirPanel('buscar')}
            data-testid={`ingesta-vincular-btn-${item.dominio}`}>
            <Building2 className="w-3.5 h-3.5 mr-1" /> Vincular
          </Button>
          {/* Crear empresa nueva directamente */}
          <Button size="sm" variant="outline"
            className="h-7 text-xs text-emerald-700 border-emerald-300 hover:bg-emerald-50"
            onClick={() => abrirPanel('crear')}
            data-testid={`ingesta-crear-btn-${item.dominio}`}>
            + Crear
          </Button>
          {/* Ignorar — dropdown dos opciones */}
          <div className="relative">
            <button type="button"
              className="flex items-center gap-0.5 text-[11px] text-ink-400 hover:text-ink-700 disabled:opacity-50 whitespace-nowrap"
              onClick={(e) => { e.stopPropagation(); setIgnorarOpts((x) => !x); }}
              disabled={ignorando}
              data-testid={`ingesta-ignorar-btn-${item.dominio}`}>
              {ignorando
                ? <Loader2 className="w-3 h-3 animate-spin" />
                : <>Ignorar <ChevronDown className="w-3 h-3" /></>}
            </button>
            {ignorarOpts && (
              <div className="absolute right-0 top-full mt-1 bg-white border border-ink-200 rounded shadow-lg z-[9999] w-52 py-1"
                onClick={(e) => e.stopPropagation()}>
                <button type="button"
                  className="w-full text-left px-3 py-2 text-xs hover:bg-ink-50 text-ink-700"
                  onClick={() => { setIgnorarOpts(false); ignorarAhora(); }}
                  data-testid={`ingesta-ignorar-ahora-${item.dominio}`}>
                  <span className="font-medium">Ignorar ahora</span>
                  <span className="block text-[10px] text-ink-400 mt-0.5">Vuelve a aparecer en la próxima carga</span>
                </button>
                <div className="border-t border-ink-100 my-0.5" />
                <button type="button"
                  className="w-full text-left px-3 py-2 text-xs hover:bg-red-50 text-red-700"
                  onClick={() => { setIgnorarOpts(false); ignorarPermanente(); }}
                  data-testid={`ingesta-ignorar-permanente-${item.dominio}`}>
                  <span className="font-medium">No importar nunca</span>
                  <span className="block text-[10px] text-red-400 mt-0.5">Bloquea este dominio permanentemente</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {expandido && (
        <div className="px-4 pb-3">
          <DominioAccionPanel
            key={modoPanel}
            dominio={item.dominio}
            modoInicial={modoPanel}
            onDecision={(dom, dec, nombre) => { setExpandido(false); onDecision(dom, dec, nombre); }}
            onCancelar={() => setExpandido(false)}
          />
        </div>
      )}
    </div>
  );
}


// ─── Fila de remitente (tab Contactos) ─────────────────────────────────────
function RemitenteRow({ item, onCrear, onIgnorar }) {
  const [expandido, setExpandido] = useState(false);
  const [form, setForm] = useState({
    nombre: (item.from_name || '').trim().split(/\s+/)[0] || '',
    apellidos: (item.from_name || '').trim().split(/\s+/).slice(1).join(' '),
    cargo: '',
  });
  // Empresa seleccionada para el contacto
  const [empresaId, setEmpresaId] = useState(item.entidad_id || null);
  const [empresaNombre, setEmpresaNombre] = useState(item.entidad_nombre || '');
  const [empresaQuery, setEmpresaQuery] = useState('');
  const [empresaResultados, setEmpresaResultados] = useState([]);
  const [empresaBuscando, setEmpresaBuscando] = useState(false);
  const empresaTimer = useRef(null);
  const [creando, setCreando] = useState(false);
  const [ignorando, setIgnorando] = useState(false);
  const [extrayendoIA, setExtrayendoIA] = useState(false);

  const extraerConIA = async () => {
    setExtrayendoIA(true);
    try {
      const r = await req('/api/correo/ai/extraer-datos-contacto', {
        method: 'POST',
        body: JSON.stringify({ from_address: item.from_address, from_name: item.from_name || '' }),
      });
      setForm({
        nombre: r.nombre || form.nombre,
        apellidos: r.apellidos || form.apellidos,
        cargo: r.cargo || '',
      });
      if (r.departamento) toast.success(`Extraído: ${[r.cargo, r.departamento].filter(Boolean).join(' · ')}`);
      else if (r.cargo) toast.success(`Cargo encontrado: ${r.cargo}`);
      else toast.info('No se encontraron datos adicionales en la firma del correo');
    } catch (e) {
      toast.error(`IA: ${e.message}`);
    } finally { setExtrayendoIA(false); }
  };

  const buscarEmpresa = (q) => {
    setEmpresaQuery(q);
    if (!empresaId) setEmpresaNombre(q);
    clearTimeout(empresaTimer.current);
    if (!q.trim()) { setEmpresaResultados([]); return; }
    empresaTimer.current = setTimeout(async () => {
      setEmpresaBuscando(true);
      try {
        const d = await req(`/api/entidades/autocomplete?q=${encodeURIComponent(q.trim())}&limit=5`);
        setEmpresaResultados(Array.isArray(d) ? d : (d.items || []));
      } catch (_) { setEmpresaResultados([]); }
      finally { setEmpresaBuscando(false); }
    }, 300);
  };

  const confirmarCrear = async () => {
    if (!form.nombre.trim()) { return; }
    setCreando(true);
    try {
      await req('/api/contactos', {
        method: 'POST',
        body: JSON.stringify({
          nombre: form.nombre.trim(),
          apellidos: form.apellidos.trim() || undefined,
          cargo: form.cargo.trim() || undefined,
          email_trabajo: item.from_address,
          entidad_id: empresaId || undefined,
          origen: 'enriquecimiento_correo',
        }),
      });
      onCrear(item.from_address, form.nombre.trim());
    } catch (e) {
      toast.error(`Crear contacto: ${e.message}`);
    } finally { setCreando(false); }
  };

  const ignorar = async () => {
    setIgnorando(true);
    try {
      await req('/api/correo/ingesta/contactos/ignorar', {
        method: 'POST',
        body: JSON.stringify({ from_address: item.from_address }),
      });
      onIgnorar(item.from_address);
    } catch (e) {
      toast.error(`Ignorar: ${e.message}`);
      setIgnorando(false);
    }
  };

  return (
    <div className="border border-ink-100 rounded-lg bg-white" data-testid={`remitente-row-${item.from_address}`}>
      <div className="flex items-center gap-3 px-4 py-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-sm text-ink-900">{item.from_name || item.from_address}</span>
            <Badge className="bg-blue-100 text-blue-800 border-blue-200 border text-[10px]">
              {item.count} {item.count === 1 ? 'correo' : 'correos'}
            </Badge>
            {item.entidad_nombre && (
              <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 border text-[10px]">
                {item.entidad_nombre}
              </Badge>
            )}
          </div>
          <p className="text-[11px] text-ink-500 mt-0.5">{item.from_address}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button size="sm" variant="outline"
            className="h-7 text-xs text-blue-700 border-blue-300 hover:bg-blue-50"
            onClick={() => setExpandido((x) => !x)}
            data-testid={`remitente-crear-btn-${item.from_address}`}>
            <Users className="w-3.5 h-3.5 mr-1" /> Crear contacto
            {expandido ? <ChevronUp className="w-3 h-3 ml-1" /> : <ChevronDown className="w-3 h-3 ml-1" />}
          </Button>
          <button type="button"
            className="text-[11px] text-ink-400 hover:text-ink-700 disabled:opacity-50"
            onClick={ignorar} disabled={ignorando}
            data-testid={`remitente-ignorar-btn-${item.from_address}`}>
            {ignorando ? <Loader2 className="w-3 h-3 animate-spin inline mr-0.5" /> : null}
            Ignorar
          </button>
        </div>
      </div>

      {expandido && (
        <div className="px-4 pb-3">
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg space-y-2">
            {/* Botón IA — extrae datos del remitente leyendo la firma del correo */}
            <button type="button"
              className="w-full flex items-center justify-center gap-1.5 py-1.5 rounded border border-violet-200 bg-violet-50 text-xs font-medium text-violet-700 hover:bg-violet-100 transition disabled:opacity-40"
              onClick={extraerConIA} disabled={extrayendoIA}
              data-testid="remitente-extraer-ia">
              {extrayendoIA
                ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Leyendo firma del correo…</>
                : <><Sparkles className="w-3.5 h-3.5" /> Extraer datos con IA (nombre, cargo, teléfono)</>}
            </button>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] text-ink-500 uppercase font-semibold block mb-0.5">Nombre *</label>
                <Input className="h-8 text-sm" value={form.nombre}
                  onChange={(e) => setForm((f) => ({ ...f, nombre: e.target.value }))}
                  placeholder="Nombre" data-testid="remitente-nombre" />
              </div>
              <div>
                <label className="text-[10px] text-ink-500 uppercase font-semibold block mb-0.5">Apellidos</label>
                <Input className="h-8 text-sm" value={form.apellidos}
                  onChange={(e) => setForm((f) => ({ ...f, apellidos: e.target.value }))}
                  placeholder="Apellidos" data-testid="remitente-apellidos" />
              </div>
            </div>
            <div>
              <label className="text-[10px] text-ink-500 uppercase font-semibold block mb-0.5">Cargo (opcional)</label>
              <Input className="h-8 text-sm" value={form.cargo}
                onChange={(e) => setForm((f) => ({ ...f, cargo: e.target.value }))}
                placeholder="Cargo" data-testid="remitente-cargo" />
            </div>
            {/* Empresa con autocomplete */}
            <div className="relative">
              <label className="text-[10px] text-ink-500 uppercase font-semibold block mb-0.5">Empresa</label>
              <Input className="h-8 text-sm"
                value={empresaId ? empresaNombre : empresaQuery}
                placeholder="Buscar empresa…"
                onChange={(e) => { setEmpresaId(null); buscarEmpresa(e.target.value); }}
                onFocus={() => { if (empresaId) { setEmpresaId(null); setEmpresaQuery(empresaNombre); } }}
                data-testid="remitente-empresa" />
              {empresaBuscando && <p className="text-[10px] text-ink-400 mt-0.5">Buscando…</p>}
              {empresaResultados.length > 0 && !empresaId && (
                <div className="absolute top-full left-0 right-0 z-[9999] bg-white border border-ink-200 rounded shadow-md mt-0.5 max-h-32 overflow-y-auto">
                  {empresaResultados.map((r) => (
                    <button key={r.id} type="button"
                      className="w-full text-left px-3 py-1.5 text-xs hover:bg-brand-50 border-b border-ink-50 last:border-0"
                      onClick={() => { setEmpresaId(r.id); setEmpresaNombre(r.nombre_comercial || r.razon_social); setEmpresaResultados([]); }}>
                      {r.nombre_comercial || r.razon_social}
                      {r.cif && <span className="ml-1 text-ink-400">{r.cif}</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="text-[11px] text-ink-500">
              Email: <span className="font-medium">{item.from_address}</span>
            </div>
            <div className="flex justify-between items-center pt-1">
              <button type="button" className="text-[11px] text-ink-400 hover:text-ink-600"
                onClick={() => setExpandido(false)}>Cancelar</button>
              <Button size="sm" className="h-7 text-xs"
                onClick={confirmarCrear} disabled={!form.nombre.trim() || creando}
                data-testid="remitente-confirmar">
                {creando ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Crear contacto'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ─── Página principal ──────────────────────────────────────────────────────
export default function CorreosIngesta() {
  const navigate = useNavigate();
  const [tabActivo, setTabActivo] = useState('empresas'); // 'empresas' | 'contactos'

  // Tab Empresas
  const [dominios, setDominios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCorreos, setTotalCorreos] = useState(0);
  const [seleccionados, setSeleccionados] = useState(new Set());
  const [ignorandoLote, setIgnorandoLote] = useState(false);

  // Tab Contactos
  const [remitentes, setRemitentes] = useState([]);
  const [loadingContactos, setLoadingContactos] = useState(false);

  // Enriquecimiento masivo IA
  const [enriqueciendoIA, setEnriqueciendoIA] = useState(false);
  const [resultadosIA, setResultadosIA] = useState(null); // null | { resultados: [] }
  const [aceptadosIA, setAceptadosIA] = useState(new Set()); // dominios aceptados
  const [aplicandoIA, setAplicandoIA] = useState(false);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const d = await req('/api/correo/ingesta/dominios');
      setDominios(d.items || []);
      setTotalCorreos(d.total_correos_sin_vincular || 0);
    } catch (e) {
      toast.error(`Ingesta: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  const cargarContactos = useCallback(async () => {
    setLoadingContactos(true);
    try {
      const d = await req('/api/correo/ingesta/contactos');
      setRemitentes(d.items || []);
    } catch (e) {
      toast.error(`Contactos: ${e.message}`);
    } finally {
      setLoadingContactos(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => { if (tabActivo === 'contactos') cargarContactos(); }, [tabActivo, cargarContactos]);

  const toggleSeleccion = (dominio) => {
    setSeleccionados((prev) => {
      const next = new Set(prev);
      if (next.has(dominio)) next.delete(dominio);
      else next.add(dominio);
      return next;
    });
  };

  const toggleTodos = () => {
    if (seleccionados.size === dominios.length) {
      setSeleccionados(new Set());
    } else {
      setSeleccionados(new Set(dominios.map((d) => d.dominio)));
    }
  };

  const onDecision = (dominio, decision, nombreEmpresa) => {
    setDominios((prev) => prev.filter((d) => d.dominio !== dominio));
    setSeleccionados((prev) => { const next = new Set(prev); next.delete(dominio); return next; });
    if (decision === 'ignorar_ahora') {
      toast.info(`@${dominio} ocultado — reaparecerá en la próxima carga`);
    } else if (decision === 'ignorar_permanente') {
      toast.success(`@${dominio} bloqueado permanentemente`);
    } else {
      toast.success(`@${dominio} → ${nombreEmpresa || 'empresa'}`);
    }
  };

  const ignorarLote = async () => {
    if (seleccionados.size === 0) return;
    setIgnorandoLote(true);
    try {
      const items = Array.from(seleccionados).map((d) => ({ domain: d, decision: 'ignorar' }));
      const r = await req('/api/correo/ingesta/aplicar-lote', {
        method: 'POST',
        body: JSON.stringify({ items }),
      });
      toast.success(`${r.procesados} dominio(s) ignorado(s)${r.errores > 0 ? ` · ${r.errores} error(es)` : ''}`);
      setDominios((prev) => prev.filter((d) => !seleccionados.has(d.dominio)));
      setSeleccionados(new Set());
    } catch (e) {
      toast.error(`Lote: ${e.message}`);
    } finally {
      setIgnorandoLote(false);
    }
  };

  const todosMarcados = dominios.length > 0 && seleccionados.size === dominios.length;

  const analizarConIA = async () => {
    if (dominios.length === 0) { toast.info('No hay dominios pendientes'); return; }
    setEnriqueciendoIA(true);
    setResultadosIA(null);
    try {
      const lote = dominios.slice(0, 20).map((d) => d.dominio);
      const r = await req('/api/correo/ingesta/enriquecer-masivo', {
        method: 'POST',
        body: JSON.stringify({ domains: lote }),
      });
      setResultadosIA(r);
      // Pre-aceptar dominios donde la IA encontró algo (empresa en CRM o datos suficientes)
      const preAceptados = new Set(
        (r.resultados || [])
          .filter((x) => !x.error && (x.razon_social || x.cif))
          .map((x) => x.domain)
      );
      setAceptadosIA(preAceptados);
    } catch (e) {
      toast.error(`IA masiva: ${e.message}`);
    } finally {
      setEnriqueciendoIA(false);
    }
  };

  const aplicarSugerenciasIA = async () => {
    if (aceptadosIA.size === 0) { toast.info('No hay sugerencias aceptadas'); return; }
    setAplicandoIA(true);
    try {
      const resultados = resultadosIA?.resultados || [];
      const items = resultados
        .filter((r) => aceptadosIA.has(r.domain))
        .map((r) => {
          if (r.en_crm && r.entidad_id) {
            return { domain: r.domain, decision: 'vincular', entidad_id: r.entidad_id };
          }
          // Crear empresa nueva con los datos encontrados
          return {
            domain: r.domain,
            decision: 'crear',
            crear_empresa: {
              razon_social: r.razon_social || r.domain,
              cif: r.cif || undefined,
              dominio_email: r.domain,
              logo_url: r.web ? `https://logo.clearbit.com/${r.domain}` : undefined,
            },
          };
        });

      const res = await req('/api/correo/ingesta/aplicar-lote', {
        method: 'POST',
        body: JSON.stringify({ items }),
      });
      toast.success(`${res.procesados} dominio(s) procesados${res.errores > 0 ? ` · ${res.errores} error(es)` : ''}`);
      setResultadosIA(null);
      setAceptadosIA(new Set());
      cargar(); // recargar lista de dominios pendientes
    } catch (e) {
      toast.error(`Aplicar: ${e.message}`);
    } finally {
      setAplicandoIA(false);
    }
  };

  return (
    <div className="h-[calc(100vh-3.5rem)] flex flex-col bg-ink-50/30" data-testid="correos-ingesta-page">

      {/* ─ Header ─────────────────────────────────────────────── */}
      <div className="flex items-center gap-3 px-6 py-3 bg-white border-b border-ink-200">
        <button
          onClick={() => navigate('/correos')}
          className="text-ink-500 hover:text-ink-900 flex items-center gap-1 text-sm"
          data-testid="ingesta-back"
        >
          <ArrowLeft className="w-4 h-4" /> Correos
        </button>
        <div className="h-4 w-px bg-ink-200" />
        <div className="flex-1">
          <h1 className="text-base font-semibold text-ink-900">Cuadro de mando de ingesta</h1>
          {!loading && (
            <p className="text-xs text-ink-500">
              {dominios.length} dominio(s) pendientes · {totalCorreos} correos sin empresa
            </p>
          )}
        </div>
        <Button size="sm" variant="outline" className="h-8" onClick={cargar} disabled={loading}>
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><RefreshCw className="w-3.5 h-3.5 mr-1" />Actualizar</>}
        </Button>
        {tabActivo === 'empresas' && dominios.length > 0 && (
          <Button size="sm"
            className="h-8 bg-violet-600 hover:bg-violet-700 text-white"
            onClick={analizarConIA} disabled={enriqueciendoIA}
            data-testid="ingesta-analizar-ia">
            {enriqueciendoIA
              ? <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />Analizando {dominios.slice(0,20).length} dominios…</>
              : <><Sparkles className="w-3.5 h-3.5 mr-1" />Analizar con IA ({Math.min(dominios.length, 20)})</>}
          </Button>
        )}
      </div>

      {/* ─ Tabs ─────────────────────────────────────────────────── */}
      <div className="flex border-b border-ink-200 bg-white px-6">
        <button
          onClick={() => setTabActivo('empresas')}
          className={`px-4 py-2.5 text-sm font-medium flex items-center gap-1.5 border-b-2 -mb-px transition-colors ${tabActivo === 'empresas' ? 'text-brand-600 border-brand-600' : 'text-ink-500 border-transparent hover:text-ink-700'}`}
        >
          <Building2 className="w-4 h-4" /> Empresas
          {dominios.length > 0 && (
            <span className="bg-orange-100 text-orange-700 text-[10px] font-semibold px-1.5 py-0.5 rounded-full">
              {dominios.length}
            </span>
          )}
        </button>
        <button
          onClick={() => setTabActivo('contactos')}
          className={`px-4 py-2.5 text-sm font-medium flex items-center gap-1.5 border-b-2 -mb-px transition-colors ${tabActivo === 'contactos' ? 'text-brand-600 border-brand-600' : 'text-ink-500 border-transparent hover:text-ink-700'}`}
        >
          <Users className="w-4 h-4" /> Contactos
          {remitentes.length > 0 && (
            <span className="bg-blue-100 text-blue-700 text-[10px] font-semibold px-1.5 py-0.5 rounded-full">
              {remitentes.length}
            </span>
          )}
        </button>
      </div>

      {/* ─ Barra de selección (cuando hay seleccionados) ─────── */}
      {seleccionados.size > 0 && (
        <div className="flex items-center gap-3 px-6 py-2 bg-orange-50 border-b border-orange-200" data-testid="ingesta-barra-seleccion">
          <span className="text-xs text-orange-800 font-medium">{seleccionados.size} dominio(s) seleccionado(s)</span>
          <Button size="sm" variant="outline"
            className="h-7 text-xs text-red-700 border-red-300 hover:bg-red-50"
            onClick={ignorarLote} disabled={ignorandoLote}
            data-testid="ingesta-ignorar-lote">
            {ignorandoLote ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <X className="w-3.5 h-3.5 mr-1" />}
            Ignorar seleccionados
          </Button>
          <button type="button" className="text-[11px] text-ink-400 hover:text-ink-600 ml-auto"
            onClick={() => setSeleccionados(new Set())}>
            Deseleccionar todo
          </button>
        </div>
      )}

      {/* ─ Contenido ─────────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto px-6 py-4">

        {/* Tab Empresas */}
        {tabActivo === 'empresas' && (
          loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-6 h-6 animate-spin text-brand-600" />
            </div>
          ) : dominios.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <Check className="w-12 h-12 text-emerald-400 mb-3" />
              <p className="text-lg font-semibold text-ink-700">Todo en orden</p>
              <p className="text-sm text-ink-500 mt-1">No hay dominios sin empresa pendientes de revisión.</p>
              <Button size="sm" variant="outline" className="mt-4" onClick={() => navigate('/correos')}>
                <ArrowLeft className="w-4 h-4 mr-1" /> Volver a la bandeja
              </Button>
            </div>
          ) : (
            <div className="space-y-2 max-w-4xl mx-auto">
              <div className="flex items-center gap-3 px-4 py-1 text-[10px] text-ink-400 uppercase tracking-wider font-semibold">
                <button type="button" onClick={toggleTodos} className="shrink-0">
                  {todosMarcados
                    ? <CheckSquare className="w-4 h-4 text-brand-500" />
                    : <Square className="w-4 h-4" />}
                </button>
                <span className="flex-1">Dominio · Remitentes</span>
                <span className="shrink-0 w-40 text-right">Acciones</span>
              </div>
              {dominios.map((item) => (
                <DominioRow
                  key={item.dominio}
                  item={item}
                  isSelected={seleccionados.has(item.dominio)}
                  onToggle={toggleSeleccion}
                  onDecision={onDecision}
                />
              ))}
            </div>
          )
        )}

        {/* Tab Contactos */}
        {tabActivo === 'contactos' && (
          loadingContactos ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-6 h-6 animate-spin text-brand-600" />
            </div>
          ) : remitentes.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <Check className="w-12 h-12 text-emerald-400 mb-3" />
              <p className="text-lg font-semibold text-ink-700">Sin remitentes pendientes</p>
              <p className="text-sm text-ink-500 mt-1">
                Todos los remitentes de dominio corporativo ya están en tus contactos.
              </p>
            </div>
          ) : (
            <div className="space-y-2 max-w-4xl mx-auto">
              <div className="px-4 py-1 text-[10px] text-ink-400 uppercase tracking-wider font-semibold flex justify-between">
                <span>Remitente · Email</span>
                <span>Acciones</span>
              </div>
              {remitentes.map((item) => (
                <RemitenteRow
                  key={item.from_address}
                  item={item}
                  onCrear={(email, nombre) => {
                    setRemitentes((prev) => prev.filter((r) => r.from_address !== email));
                    toast.success(`Contacto "${nombre}" creado`);
                  }}
                  onIgnorar={(email) => {
                    setRemitentes((prev) => prev.filter((r) => r.from_address !== email));
                  }}
                />
              ))}
            </div>
          )
        )}

      </div>

      {/* ─── Modal resultados IA masiva ───────────────────────── */}
      {resultadosIA && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setResultadosIA(null)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-ink-200 flex items-center justify-between">
              <div>
                <h2 className="font-semibold text-ink-900 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-violet-600" /> Resultados del análisis IA
                </h2>
                <p className="text-xs text-ink-500 mt-0.5">
                  Revisa y marca qué dominios aplicar. Verde = empresa ya en CRM (vincular). Ámbar = empresa nueva (crear).
                </p>
              </div>
              <button onClick={() => setResultadosIA(null)} className="text-ink-400 hover:text-ink-700">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-3 space-y-2">
              {resultadosIA.resultados.map((r) => {
                const aceptado = aceptadosIA.has(r.domain);
                return (
                  <div key={r.domain}
                    onClick={() => {
                      if (r.error) return;
                      setAceptadosIA((prev) => {
                        const next = new Set(prev);
                        if (next.has(r.domain)) next.delete(r.domain); else next.add(r.domain);
                        return next;
                      });
                    }}
                    className={`p-3 rounded-lg border cursor-pointer transition ${
                      r.error ? 'border-red-200 bg-red-50 cursor-not-allowed' :
                      aceptado && r.en_crm ? 'border-emerald-300 bg-emerald-50' :
                      aceptado ? 'border-amber-300 bg-amber-50' :
                      'border-ink-200 bg-ink-50/50 hover:border-ink-300'
                    }`}
                    data-testid={`ia-resultado-${r.domain}`}
                  >
                    <div className="flex items-center gap-2">
                      <input type="checkbox" readOnly checked={aceptado && !r.error}
                        className="accent-violet-600 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono text-sm font-semibold text-ink-800">@{r.domain}</span>
                          {r.en_crm
                            ? <span className="text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded border border-emerald-200">✓ En CRM · Vincular</span>
                            : r.razon_social
                              ? <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded border border-amber-200">+ Crear empresa nueva</span>
                              : null}
                          {r.error && <span className="text-[10px] bg-red-100 text-red-700 px-1.5 py-0.5 rounded">Error</span>}
                        </div>
                        {r.razon_social && (
                          <p className="text-xs text-ink-700 mt-0.5">
                            {r.razon_social}
                            {r.cif && <span className="ml-2 text-ink-400">{r.cif}</span>}
                            {r.entidad_nombre && r.en_crm && <span className="ml-2 text-emerald-600">→ {r.entidad_nombre}</span>}
                          </p>
                        )}
                        {r.error && <p className="text-[10px] text-red-600 mt-0.5">{r.error}</p>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="px-5 py-3 border-t border-ink-200 flex items-center justify-between">
              <span className="text-xs text-ink-500">
                {aceptadosIA.size} de {resultadosIA.resultados.filter((r) => !r.error).length} seleccionados
              </span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setResultadosIA(null)}>Cancelar</Button>
                <Button size="sm"
                  className="bg-violet-600 hover:bg-violet-700 text-white"
                  onClick={aplicarSugerenciasIA}
                  disabled={aceptadosIA.size === 0 || aplicandoIA}
                  data-testid="ingesta-aplicar-ia">
                  {aplicandoIA
                    ? <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />Aplicando…</>
                    : <><Check className="w-3.5 h-3.5 mr-1" />Aplicar {aceptadosIA.size} sugerencia(s)</>}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
